import React, { useState, useEffect, useCallback } from 'react';
import API from './config';

const COULEURS = {
  primaire: '#1E3A5F',
  vert: '#16A34A',
  rouge: '#DC2626',
  orange: '#EA580C',
  texte: '#1F2937',
  fond: '#F7F8FA',
};

function anneeActuelleBornes() {
  const d = new Date();
  return [`${d.getFullYear()}-01-01`, `${d.getFullYear()}-12-31`];
}

function anneePrecedenteBornes() {
  const d = new Date();
  return [`${d.getFullYear() - 1}-01-01`, `${d.getFullYear() - 1}-12-31`];
}

function moisActuelBornes() {
  const d = new Date();
  const debut = new Date(d.getFullYear(), d.getMonth(), 1);
  const fin = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return [debut.toISOString().slice(0, 10), fin.toISOString().slice(0, 10)];
}

function formaterMontant(s) {
  const n = parseFloat(s || 0);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function declencherTelechargement(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function extraireFilename(r, defaut) {
  const cd = r.headers.get('content-disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return m ? m[1] : defaut;
}

export default function ExportComptable({ apiFetch }) {
  const [debut, fin] = anneeActuelleBornes();
  const [dateDebut, setDateDebut] = useState(debut);
  const [dateFin, setDateFin] = useState(fin);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [downloading, setDownloading] = useState(null); // 'fec' | 'devis' | null

  const chargerPreview = useCallback(async () => {
    if (!dateDebut || !dateFin) return;
    setLoading(true);
    setErreur(null);
    try {
      const params = new URLSearchParams({ date_debut: dateDebut, date_fin: dateFin });
      const r = await apiFetch(`${API}/export-comptable/preview?${params}`);
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(`Erreur ${r.status} : ${txt}`);
      }
      const data = await r.json();
      setPreview(data);
    } catch (e) {
      setErreur(e.message || 'Erreur de chargement');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, dateDebut, dateFin]);

  useEffect(() => { chargerPreview(); }, [chargerPreview]);

  const setPeriode = (bornes) => { setDateDebut(bornes[0]); setDateFin(bornes[1]); };

  const telechargerFEC = async () => {
    setDownloading('fec');
    setErreur(null);
    try {
      const params = new URLSearchParams({ date_debut: dateDebut, date_fin: dateFin });
      const r = await apiFetch(`${API}/export-comptable/fec?${params}`);
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(`Erreur ${r.status} : ${txt}`);
      }
      const blob = await r.blob();
      const filename = extraireFilename(r, 'FEC.txt');
      declencherTelechargement(blob, filename);
    } catch (e) {
      setErreur(e.message || 'Erreur telechargement FEC');
    } finally {
      setDownloading(null);
    }
  };

  const telechargerDevisCSV = async () => {
    setDownloading('devis');
    setErreur(null);
    try {
      const params = new URLSearchParams({ date_debut: dateDebut, date_fin: dateFin });
      const r = await apiFetch(`${API}/export-comptable/devis-acceptes.csv?${params}`);
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(`Erreur ${r.status} : ${txt}`);
      }
      const blob = await r.blob();
      const filename = extraireFilename(r, 'devis-acceptes.csv');
      declencherTelechargement(blob, filename);
    } catch (e) {
      setErreur(e.message || 'Erreur telechargement CSV');
    } finally {
      setDownloading(null);
    }
  };

  const s = {
    page: { padding: '20px', background: COULEURS.fond, minHeight: 'calc(100vh - 80px)' },
    h1: { color: COULEURS.primaire, fontSize: '24px', fontWeight: '700', margin: '0 0 6px 0' },
    sousTitre: { color: '#6B7280', fontSize: '13px', marginBottom: '20px', maxWidth: '900px' },
    card: { background: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '16px' },
    filtreRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
    label: { fontSize: '13px', fontWeight: '600', color: COULEURS.texte },
    input: { padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' },
    btn: (active = false, color = COULEURS.primaire) => ({
      background: active ? color : 'white',
      color: active ? 'white' : color,
      border: `1px solid ${color}`,
      padding: '7px 14px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
    }),
    btnDl: (color) => ({
      background: color,
      color: 'white',
      border: 'none',
      padding: '12px 20px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '700',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
    }),
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' },
    statCard: (color) => ({
      padding: '14px 16px',
      borderLeft: `4px solid ${color}`,
      background: '#F9FAFB',
      borderRadius: '6px',
    }),
    statLabel: { fontSize: '11px', textTransform: 'uppercase', color: '#6B7280', fontWeight: '600' },
    statNum: { fontSize: '28px', fontWeight: '700', color: COULEURS.texte, marginTop: '4px', fontVariantNumeric: 'tabular-nums' },
    statSub: { fontSize: '11px', color: '#9CA3AF', marginTop: '4px' },
    alert: (color, bg) => ({
      padding: '10px 14px',
      background: bg,
      border: `1px solid ${color}`,
      borderRadius: '6px',
      color,
      fontSize: '13px',
      marginBottom: '12px',
    }),
    sectionTitre: { fontSize: '15px', fontWeight: '700', color: COULEURS.primaire, marginBottom: '12px' },
    fineprint: { fontSize: '11px', color: '#6B7280', marginTop: '8px' },
  };

  const peutTelecharger = preview && !loading && !downloading;
  const totalEntites = preview
    ? preview.compteurs.factures + preview.compteurs.avoirs + preview.compteurs.paiements + preview.compteurs.remboursements_avoirs
    : 0;

  return (
    <div style={s.page}>
      <h1 style={s.h1}>📊 Export comptable</h1>
      <p style={s.sousTitre}>
        Genere un Fichier des Ecritures Comptables (FEC) norme DGFIP — article A47 A-1 LPF —
        pour transmission a l'expert comptable. Le FEC contient les factures, avoirs, paiements et
        remboursements de la periode. Les devis acceptes sont exportes separement en CSV
        (information complementaire, hors flux fiscal).
      </p>

      <div style={s.card}>
        <div style={s.sectionTitre}>📅 Periode d'export</div>
        <div style={s.filtreRow}>
          <span style={s.label}>Du</span>
          <input type="date" style={s.input} value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
          <span style={s.label}>au</span>
          <input type="date" style={s.input} value={dateFin} onChange={e => setDateFin(e.target.value)} />
          <div style={{ display: 'flex', gap: '6px', marginLeft: '8px' }}>
            <button style={s.btn(false)} onClick={() => setPeriode(anneeActuelleBornes())}>Annee en cours</button>
            <button style={s.btn(false)} onClick={() => setPeriode(anneePrecedenteBornes())}>Annee precedente</button>
            <button style={s.btn(false)} onClick={() => setPeriode(moisActuelBornes())}>Mois en cours</button>
          </div>
          <button
            style={{ ...s.btn(true), marginLeft: 'auto' }}
            onClick={chargerPreview}
            disabled={loading}
          >
            {loading ? '⏳ Chargement...' : '🔄 Rafraichir l\'apercu'}
          </button>
        </div>
      </div>

      {erreur && (
        <div style={s.alert(COULEURS.rouge, '#FEE2E2')}>
          ⚠️ {erreur}
        </div>
      )}

      {preview && (
        <>
          <div style={s.card}>
            <div style={s.sectionTitre}>📋 Apercu du contenu sur la periode</div>
            <div style={s.grid}>
              <div style={s.statCard('#1E3A5F')}>
                <div style={s.statLabel}>Factures</div>
                <div style={s.statNum}>{preview.compteurs.factures}</div>
                <div style={s.statSub}>{formaterMontant(preview.totaux_ttc.factures)} TTC</div>
              </div>
              <div style={s.statCard('#7C3AED')}>
                <div style={s.statLabel}>Avoirs</div>
                <div style={s.statNum}>{preview.compteurs.avoirs}</div>
                <div style={s.statSub}>{formaterMontant(preview.totaux_ttc.avoirs)} TTC</div>
              </div>
              <div style={s.statCard('#16A34A')}>
                <div style={s.statLabel}>Paiements</div>
                <div style={s.statNum}>{preview.compteurs.paiements}</div>
                <div style={s.statSub}>{formaterMontant(preview.totaux_ttc.paiements)} encaisses</div>
              </div>
              <div style={s.statCard('#DC2626')}>
                <div style={s.statLabel}>Remboursements</div>
                <div style={s.statNum}>{preview.compteurs.remboursements_avoirs}</div>
                <div style={s.statSub}>{formaterMontant(preview.totaux_ttc.remboursements)} verses</div>
              </div>
              <div style={s.statCard('#EA580C')}>
                <div style={s.statLabel}>Devis acceptes</div>
                <div style={s.statNum}>{preview.compteurs.devis_acceptes}</div>
                <div style={s.statSub}>info complementaire</div>
              </div>
            </div>
            <div style={s.fineprint}>
              SIREN : <strong>{preview.config.siren}</strong> · Regime TVA :{' '}
              <strong>{preview.config.tva_assujetti ? 'Assujetti' : 'Franchise en base (art. 293 B CGI)'}</strong>
            </div>
          </div>

          <div style={s.card}>
            <div style={s.sectionTitre}>📥 Telechargements</div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                style={{
                  ...s.btnDl(COULEURS.primaire),
                  opacity: peutTelecharger && totalEntites > 0 ? 1 : 0.5,
                  cursor: peutTelecharger && totalEntites > 0 ? 'pointer' : 'not-allowed',
                }}
                onClick={telechargerFEC}
                disabled={!peutTelecharger || totalEntites === 0}
                title={totalEntites === 0 ? 'Aucune ecriture sur la periode' : 'Telecharger le FEC norme DGFIP'}
              >
                {downloading === 'fec' ? '⏳ Generation...' : '📄 Telecharger FEC (.txt)'}
              </button>
              <button
                style={{
                  ...s.btnDl(COULEURS.orange),
                  opacity: peutTelecharger && preview.compteurs.devis_acceptes > 0 ? 1 : 0.5,
                  cursor: peutTelecharger && preview.compteurs.devis_acceptes > 0 ? 'pointer' : 'not-allowed',
                }}
                onClick={telechargerDevisCSV}
                disabled={!peutTelecharger || preview.compteurs.devis_acceptes === 0}
                title={preview.compteurs.devis_acceptes === 0 ? 'Aucun devis accepte sur la periode' : 'Telecharger CSV devis acceptes'}
              >
                {downloading === 'devis' ? '⏳ Generation...' : '📋 Devis acceptes (.csv)'}
              </button>
            </div>
            <div style={s.fineprint}>
              ℹ️ Le FEC suit la norme DGFIP : 18 colonnes, separateur tabulation, UTF-8, equilibre debit/credit garanti.
              Nom de fichier normalise : <code>{preview.config.siren}FEC{dateFin.replace(/-/g, '')}.txt</code>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
